/**
 * A1.2 — Session d'assistance (token dédié en sessionStorage).
 * Ne touche jamais au JWT Super Admin en localStorage.
 * Isolation native par onglet via sessionStorage.
 */

export const ASSISTANCE_SESSION_KEY = 'clickorico_assistance_session';
/** Flag historique lu par permissionDefinitions (vue commerçant). */
export const ASSISTANCE_FLAG_KEY = 'clickorico_impersonating';

function buildContext(commercant) {
  if (!commercant?.id) return null;
  return {
    commercantId: commercant.id,
    commercantNom: commercant.nom || 'Boutique',
    role: 'COMMERCANT',
    permissions: Array.isArray(commercant.permissions) ? commercant.permissions : [],
  };
}

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isExpired(expiresAt) {
  if (!expiresAt) return true;
  const ts = Date.parse(expiresAt);
  if (Number.isNaN(ts)) return true;
  return Date.now() >= ts;
}

/**
 * Purge immédiate et synchrone du contexte d'assistance.
 * À appeler avant tout appel API / WebSocket si la session est expirée.
 */
export function clearAssistanceSession() {
  try {
    sessionStorage.removeItem(ASSISTANCE_SESSION_KEY);
    sessionStorage.removeItem(ASSISTANCE_FLAG_KEY);
  } catch {
    // sessionStorage indisponible (tests / mode privé strict)
  }
}

/**
 * Lit la session brute sans vérifier l'expiration.
 * @returns {{ token: string, expiresAt: string, context: object } | null}
 */
export function peekAssistanceSession() {
  try {
    const data = safeParse(sessionStorage.getItem(ASSISTANCE_SESSION_KEY));
    if (!data?.token || !data?.expiresAt || !data?.context?.commercantId) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

/**
 * Garantit une session fraîche : si expiresAt est dépassé, purge synchrone
 * et retourne null. Aucun appel réseau ici.
 * @returns {{ token: string, expiresAt: string, context: object } | null}
 */
export function ensureAssistanceSessionFresh() {
  const session = peekAssistanceSession();
  if (!session) {
    // Flag orphelin éventuel
    try {
      if (sessionStorage.getItem(ASSISTANCE_FLAG_KEY) === '1') {
        sessionStorage.removeItem(ASSISTANCE_FLAG_KEY);
      }
    } catch {
      /* ignore */
    }
    return null;
  }
  if (isExpired(session.expiresAt)) {
    clearAssistanceSession();
    return null;
  }
  return session;
}

export function isAssistanceActive() {
  return Boolean(ensureAssistanceSessionFresh());
}

export function getAssistanceToken() {
  return ensureAssistanceSessionFresh()?.token || null;
}

export function getAssistanceContext() {
  return ensureAssistanceSessionFresh()?.context || null;
}

export function getAssistanceCommercantId() {
  return ensureAssistanceSessionFresh()?.context?.commercantId || null;
}

export function getAssistanceExpiresAt() {
  return ensureAssistanceSessionFresh()?.expiresAt || null;
}

/**
 * Contexte d'assistance commerçant actif (pas une réduction de droits).
 * Le rôle réel reste SUPER_ADMIN ; seul le commerçant courant change.
 */
export function isMerchantAssistanceContext() {
  return Boolean(getAssistanceCommercantId());
}

function normalizeSessionRole(sessionUser) {
  const role = sessionUser?.role;
  if (role === 'super-admin') return 'SUPER_ADMIN';
  return role || null;
}

function sessionCommercantId(sessionUser) {
  return sessionUser?.commercantId || sessionUser?.commercant?.id || null;
}

/**
 * Portée opérationnelle Admin : boutique courante vs vue plateforme.
 * En assistance → toujours scopé au commerçant impersonné.
 * @param {object|null} sessionUser admin_user / getStoredUser()
 * @returns {{ scopedToMerchant: boolean, commercantId: string|null, isAssistance: boolean }}
 */
export function resolveMerchantOperationalScope(sessionUser) {
  const assistanceId = getAssistanceCommercantId();
  if (assistanceId) {
    return {
      scopedToMerchant: true,
      commercantId: assistanceId,
      isAssistance: true,
    };
  }
  const role = normalizeSessionRole(sessionUser);
  const id = sessionCommercantId(sessionUser);
  if (role === 'COMMERCANT' && id) {
    return {
      scopedToMerchant: true,
      commercantId: id,
      isAssistance: false,
    };
  }
  return {
    scopedToMerchant: false,
    commercantId: id,
    isAssistance: false,
  };
}

export function isMerchantScopedSession(sessionUser) {
  return resolveMerchantOperationalScope(sessionUser).scopedToMerchant;
}

export function getEffectiveCommercantId(sessionUser) {
  return resolveMerchantOperationalScope(sessionUser).commercantId;
}

/**
 * Catalogue ingrédients plateforme en lecture seule :
 * SUPER_ADMIN hors assistance uniquement.
 * En assistance → CRUD complet (droits SA non diminués, contexte boutique).
 */
export function isPlatformIngredientsReadOnly(sessionUser) {
  if (isMerchantAssistanceContext()) return false;
  const role = normalizeSessionRole(sessionUser);
  return role === 'SUPER_ADMIN';
}

/**
 * Démarre une session d'assistance à partir de la réponse API A1.1.
 * @param {{ assistanceToken: string, expiresAt: string, commercant: object }} response
 */
export function startAssistanceSession(response) {
  const token = response?.assistanceToken;
  const expiresAt = response?.expiresAt;
  const context = buildContext(response?.commercant);
  if (!token || !expiresAt || !context) {
    throw new Error('Réponse assistance invalide (token / expiresAt / commercant manquant)');
  }
  if (isExpired(expiresAt)) {
    throw new Error('Token assistance déjà expiré');
  }
  const payload = { token, expiresAt, context };
  sessionStorage.setItem(ASSISTANCE_SESSION_KEY, JSON.stringify(payload));
  sessionStorage.setItem(ASSISTANCE_FLAG_KEY, '1');
  return context;
}

/**
 * Restauration F5 : contexte UI si session encore valide, sinon null (déjà purgé).
 */
export function restoreAssistanceContext() {
  return ensureAssistanceSessionFresh()?.context || null;
}
